<template>
    <v-dialog v-model="showDialog" :scrim="false" :persistent="loading" scrollable>
        <template v-slot:activator="{props}">
            <v-btn v-if="canEdit" variant="text" icon="mdi-pencil" size="small" color="primary" v-bind="props" />
        </template>
        <v-card v-if="!bookingFieldsLoading">
            <v-card-title class="text-white bg-primary">
                {{ $t('travelers_for_reservation') }}
            </v-card-title>
            <v-card-text>
                <div v-for="(tourist, index) in tourists" :key="index" class="mt-5">
                    <tourist-form
                        :ref="`tourist${index}`"
                        v-model="tourists[index]"
                        :index="index"
                        label="tourist"
                        :disabled="loading"
                        :hide-index="tourists.length === 1"
                        :selected-tourists="tourists"
                        :start-date="startDate"
                        name-not-required
                        full-width
                        @person-selected="personSelected($event)"
                    />
                    <tourist-passport-form
                        :index="index"
                        :disabled="loading"
                        expanded-all
                        @set-prop="onTouristsChange"
                    />
                </div>
            </v-card-text>
            <v-card-actions>
                <div @click="validateTouristsWithErrors">
                    <v-btn color="primary" :disabled="!allTouristsValid || loading" :loading="loading" @click="save">
                        {{ $t('save') }}
                    </v-btn>
                </div>
                <v-btn variant="text" :disabled="loading" @click="showDialog = false">{{ $t('cancel') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, mixins, Prop, Watch} from 'vue-facing-decorator'
    import TouristPassportForm from '~src/components/booking/forms/touristPassportForm.src'
    import TouristForm from '~src/components/booking/forms/touristForm.src'
    import {BookingTouristsMixin} from '~src/components/booking/mixins/BookingTouristsMixin.vue'
    import {FillServiceBookingDataMixin} from '@/components/account/mixins/FillServiceBookingDataMixin.vue'
    import OfflineServiceMixin from '~src/components/account/offlineServices/mixins/OfflineServiceMixin.vue'
    import fillBookingDataRQAdapter from '@/adapters/fillBookingDataRQAdapter'

    const optionalTouristBookingFields = () => ({
        bookingFields: {
            BIRTHDATE: {
                mainTouristStatus: 'OPTIONAL',
                allTouristsStatus: 'OPTIONAL',
                childrenStatus: 'OPTIONAL',
            },
            CITIZENSHIP: {
                mainTouristStatus: 'OPTIONAL',
                allTouristsStatus: 'OPTIONAL',
                childrenStatus: 'OPTIONAL',
            },
            EMAIL: {
                mainTouristStatus: 'OPTIONAL',
                allTouristsStatus: 'OPTIONAL',
                childrenStatus: 'OPTIONAL',
            },
            PASSPORT_EXPIRE_DATE: {
                mainTouristStatus: 'OPTIONAL',
                allTouristsStatus: 'OPTIONAL',
                childrenStatus: 'OPTIONAL',
            },
            PASSPORT_ISSUE_DATE: {
                mainTouristStatus: 'DISABLED',
                allTouristsStatus: 'DISABLED',
                childrenStatus: 'DISABLED',
            },
            PASSPORT_NUMBER: {
                mainTouristStatus: 'OPTIONAL',
                allTouristsStatus: 'OPTIONAL',
                childrenStatus: 'OPTIONAL',
            },
            PHONE: {
                mainTouristStatus: 'OPTIONAL',
                allTouristsStatus: 'OPTIONAL',
                childrenStatus: 'OPTIONAL',
            },
            PREFIX: {
                mainTouristStatus: 'OPTIONAL',
                allTouristsStatus: 'OPTIONAL',
                childrenStatus: 'OPTIONAL',
            },
        },
        supplierConfigDetails: {
            tourLeadEmailRequired: false,
            guestsInformationRequiredType: 'MIXED',
        },
    })
    const fillProductBookingDataMethodName = serviceType => {
        if (serviceType === 'OWNCHARTER') {
            serviceType = 'FLIGHT'
        } else if (serviceType === 'OWNEXTRASERVICE') {
            serviceType = 'EXTRASERVICE'
        } else if (serviceType === 'CARRENT') {
            serviceType = 'CAR'
        } else if (serviceType === 'OWNVISA') {
            serviceType = 'VISA'
        } else if (serviceType === 'EXTRASERVICE') {
            serviceType = 'OFFLINE'
        }
        return `fill${serviceType.charAt(0)}${serviceType.slice(1).toLowerCase()}BookingData`
    }

    // TODO Need refactoring with Fill{Product}DataModal and each other
    @Component({
        components: {TouristForm, TouristPassportForm},
    })
    export class OrderTravelersDialog extends mixins(BookingTouristsMixin, FillServiceBookingDataMixin) {
        @Prop({required: true, type: Object}) order
        @Prop({required: true, type: Array}) orderTravelers

        showDialog = false
        loading = false
        allTouristsValid = false

        @Watch('showDialog')
        async onShowDialog(showDialog) {
            if (showDialog) {
                this.loading = false
                this.bookingFieldsLoading = true
                runtimeStore.SET_TOURIST_BOOKING_FIELDS(optionalTouristBookingFields())
                persistentStore.SET_TOURISTS([])
                this.setTourists(this.orderTravelers)
                this.bookingFieldsLoading = false
                await this.$nextTick()
                this.setAllTouristsValid()
            } else {
                runtimeStore.SET_TOURIST_BOOKING_FIELDS({bookingFields: {}})
            }
        }

        @Watch('tourists', {deep: true})
        async onTouristsChange() {
            await this.$nextTick()
            this.setAllTouristsValid()
        }

        async setAllTouristsValid() {
            const validation = []
            for (const t of this.tourists) {
                const i = this.tourists.indexOf(t)
                const validate = this.$refs[`tourist${i}`]?.[0]?.$refs?.['form']?.validate
                const {valid} = validate ? await validate() : {valid: false}
                validation.push(valid)
                this.$refs[`tourist${i}`]?.[0]?.$refs?.['form']?.resetValidation()
            }
            this.allTouristsValid = validation.every(el => el === true)
        }

        validateTouristsWithErrors() {
            this.tourists.forEach((t, i) => {
                this.$refs[`tourist${i}`]?.[0]?.$refs?.['form']?.validate()
            })
        }

        personSelected({person, index}) {
            if (person) {
                const val = runtimeStore.touristFromPerson(person)
                persistentStore.SET_TOURIST({index, val})
            } else {
                persistentStore.SET_TOURIST_PROP({index, prop: 'personId', val: null})
            }
        }

        async save() {
            this.loading = true
            let hasErrors = false
            const services = this.order.services.filter(
                ({status, serviceType}) =>
                    status === 'Quote' &&
                    !['EXTRASERVICE', 'OWNEXTRASERVICE', 'VISA', 'OWNVISA', 'INSURANCE'].includes(serviceType)
            )
            const extraServices = this.order.services.filter(
                ({status, serviceType}) => status === 'Quote' && serviceType === 'EXTRASERVICE'
            )
            if (services.length) {
                try {
                    const productBookingFieldsByService = await Promise.all(
                        services.map(({processId}) => this.$api.touristBookingFields.get({processId}))
                    )
                    const results = await Promise.allSettled(
                        services.map(({serviceType, processId, travelers}, index) => {
                            const adultsCount = travelers.filter(({isChild}) => !isChild).length
                            const childrenCount = travelers.filter(({isChild}) => isChild).length
                            return this.$api[fillProductBookingDataMethodName(serviceType)].post(
                                fillBookingDataRQAdapter(
                                    {
                                        processId,
                                        tourists: this.prepareBookTourists(
                                            [
                                                ...this.adults.slice(0, adultsCount),
                                                ...this.children.slice(0, childrenCount),
                                            ],
                                            productBookingFieldsByService[index]
                                        ),
                                    },
                                    serviceType
                                )
                            )
                        })
                    )
                    if (results.some(({status}) => status === 'rejected')) {
                        hasErrors = true
                    }
                } catch (e) {
                    hasErrors = true
                }
            }

            // TODO need refactoring with OrderServiceBookModal
            if (extraServices.length) {
                const el = document.createElement('div')
                // const requestPromises = []
                for (const serviceToModify of extraServices) {
                    // TODO [P4] Need refactoring to use clean logic in composable
                    const offlineServiceMixin = useCreateComponent({
                        component: OfflineServiceMixin,
                        props: {
                            serviceToModify,
                            orderTravelers: serviceToModify.travelers,
                            orderProcessId: this.order.processId,
                            clientType: this.order.clientType,
                            customerCompanyId: this.order.customerCompanyId,
                        },
                        el,
                    })
                    await offlineServiceMixin.reset()
                    const adults = serviceToModify.travelers.filter(({isChild}) => !isChild)
                    const children = serviceToModify.travelers.filter(({isChild}) => isChild)
                    const sortedServiceTravelers = [...adults, children]
                    offlineServiceMixin.tourists = [
                        ...this.adults.slice(0, adults.length),
                        ...this.children.slice(0, children.length),
                    ].map(({prefix, firstName, lastName, birthdate, type, citizenshipId}, index) => ({
                        ...(sortedServiceTravelers[index]?.id && {id: sortedServiceTravelers[index].id}),
                        ...(prefix && {prefix}),
                        ...(firstName && {firstName}),
                        ...(lastName && {lastName}),
                        ...(birthdate && {birthdate}),
                        ...(citizenshipId && {citizenshipId}),
                        type: type.toUpperCase(),
                    }))
                    try {
                        await this.$api.orderServices.put(
                            serviceToModify.processId.replace(/^.{6}/, ''),
                            offlineServiceMixin.requestBody
                        )
                    } catch (e) {
                        hasErrors = true
                    }
                    // requestPromises.push(
                    //     this.$api.orderServices.put(
                    //         serviceToModify.processId.replace(/^.{6}/, '').split('/').join('|'),
                    //         offlineServiceMixin.requestBody
                    //     )
                    // )
                }
                el.remove()
                // TODO Need fix in API api.concurrent.modification.error
                // await Promise.allSettled(requestPromises)
            }

            await runtimeStore.refreshOrder()
            this.showDialog = false
            this.loading = false
            if (hasErrors) {
                this.$toast.error(this.$t('error_message.service_guests_save_error'))
            } else {
                this.$toast.success(this.$t('success_message.guests_saved_for_quote_services'))
            }
        }

        get searchRequest() {
            return {
                //dateTime: this.service.startDateTime.split(' ')[0],
                adults: this.orderTravelers.filter(({isChild}) => !isChild).length,
                childrenAges: this.orderTravelers.reduce((childrenAges, {isChild, dateOfBirth}) => {
                    //TODO Missing age prop in get orders
                    if (isChild) {
                        if (dateOfBirth) {
                            childrenAges.push(
                                this.$dateFns.differenceInYears(new Date(), this.$dateFns.parseISO(dateOfBirth))
                            )
                        } else {
                            childrenAges.push(4)
                        }
                    }
                    return childrenAges
                }, []),
            }
        }

        get startDate() {
            return this.$dateFns.format(new Date())
        }

        get canEdit() {
            const getName = t => (t ? `${t.name?.[0]?.firstName || ''} ${t.name?.[0]?.lastName || ''}`.trim() : null)
            const servicesForEdit = this.order.services.filter(
                ({status, serviceType}) =>
                    status === 'Quote' && !['OWNEXTRASERVICE', 'VISA', 'OWNVISA', 'INSURANCE'].includes(serviceType)
            )
            return (
                authStore.isTO1 &&
                this.orderTravelers.length > 0 &&
                servicesForEdit.length > 0 &&
                servicesForEdit
                    .sort((a, b) => a.isChild - b.isChild)
                    .every(({travelers}) => {
                        const adultsCount = travelers.filter(({isChild}) => !isChild).length
                        const childrenCount = travelers.filter(({isChild}) => isChild).length
                        const slicedOrderTravelers = [
                            ...this.orderTravelers.filter(({isChild}) => !isChild).slice(0, adultsCount),
                            ...this.orderTravelers.filter(({isChild}) => isChild).slice(0, childrenCount),
                        ]
                        return travelers.every(
                            (t, index) => !getName(t) || getName(t) === getName(slicedOrderTravelers[index])
                        )
                    })
            )
        }

        get adults() {
            return this.tourists.filter(({type}) => type === 'adult')
        }

        get children() {
            return this.tourists.filter(({type}) => type === 'child')
        }
    }

    export default OrderTravelersDialog
</script>
